import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { BarChart3, PieChart, LineChart, TrendingUp, X, Plus, Trash2 } from 'lucide-react';
import ToggleSwitch from '../ui/ToggleSwitch';
import { useToast } from '../../hooks/use-toast';

const chartTypes = [
  { id: 'bar',    name: 'Bar',    icon: BarChart3 },
  { id: 'line',   name: 'Line',   icon: LineChart },
  { id: 'pie',    name: 'Pie',    icon: PieChart },
  { id: 'donut',  name: 'Donut',  icon: PieChart },
  { id: 'area',   name: 'Area',   icon: TrendingUp },
  { id: 'column', name: 'Column', icon: BarChart3 },
];

const DEFAULT_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f59e0b', '#10b981'];

const defaultData = [
  { label: 'Jan', value: 30 },
  { label: 'Feb', value: 50 },
  { label: 'Mar', value: 80 },
  { label: 'Apr', value: 60 },
];

const ChartPanel = ({ onAddElement, onClose }) => {
  const { toast } = useToast();
  const [chartType, setChartType]           = useState('bar');
  const [chartData, setChartData]           = useState(defaultData);
  const [dataColors, setDataColors]         = useState(DEFAULT_COLORS.slice(0, defaultData.length));
  const [chartColor, setChartColor]         = useState('#6366f1');
  const [chartBackground, setChartBackground] = useState('#ffffff');
  const [xAxisTitle, setXAxisTitle]         = useState('');
  const [yAxisTitle, setYAxisTitle]         = useState('');
  const [showGridLines, setShowGridLines]   = useState(true);
  const [showLegend, setShowLegend]         = useState(true);
  const [showDataLabels, setShowDataLabels] = useState(true);
  const [legendPosition, setLegendPosition] = useState('bottom');

  const addRow = () => {
    if (chartData.length >= 10) return;
    const idx = chartData.length;
    setChartData([...chartData, { label: `Item ${idx + 1}`, value: 50 }]);
    setDataColors([...dataColors, DEFAULT_COLORS[idx % DEFAULT_COLORS.length]]);
  };

  const removeRow = (i) => {
    if (chartData.length <= 2) return;
    setChartData(chartData.filter((_, j) => j !== i));
    setDataColors(dataColors.filter((_, j) => j !== i));
  };

  const updateLabel = (i, label) => {
    const next = [...chartData];
    next[i] = { ...next[i], label };
    setChartData(next);
  };

  const updateValue = (i, raw) => {
    const next = [...chartData];
    next[i] = { ...next[i], value: Math.max(0, Number(raw) || 0) };
    setChartData(next);
  };

  const updateColor = (i, color) => {
    const next = [...dataColors];
    next[i] = color;
    setDataColors(next);
  };

  const handleAddChart = () => {
    onAddElement({
      type: 'chart',
      chartType,
      data: chartData,
      dataColors,
      color: chartColor,
      chartBackground,
      xAxisTitle,
      yAxisTitle,
      showGridLines,
      showLegend,
      showDataLabels,
      legendPosition,
      x: 80,
      y: 80,
      width: 480,
      height: 320,
    });
    toast({ title: 'Chart added', description: `${chartType} chart added to canvas` });
  };

  const isPie = chartType === 'pie' || chartType === 'donut';
  const maxVal = Math.max(...chartData.map(d => d.value), 1);

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Charts</h3>
            <p className="text-xs text-gray-500 mt-1">Add data visualizations</p>
          </div>
          {onClose && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-5">

          {/* Chart Type */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Chart Type</Label>
            <div className="grid grid-cols-3 gap-2">
              {chartTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setChartType(type.id)}
                    className={`p-2.5 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                      chartType === type.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`chart-type-${type.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[10px]">{type.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Data */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold">Data</Label>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                onClick={addRow} disabled={chartData.length >= 10}>
                <Plus className="w-3 h-3 mr-1" />Add
              </Button>
            </div>
            <div className="space-y-1.5 max-h-52 overflow-auto pr-1">
              {chartData.map((row, i) => (
                <div key={i} className="flex gap-1.5 items-center">
                  <input
                    type="color"
                    value={dataColors[i] || DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
                    onChange={(e) => updateColor(i, e.target.value)}
                    className="w-7 h-7 rounded border cursor-pointer flex-shrink-0 p-0.5"
                    title="Bar color"
                  />
                  <Input
                    placeholder="Label"
                    value={row.label}
                    onChange={(e) => updateLabel(i, e.target.value)}
                    className="h-7 text-xs flex-1 min-w-0"
                  />
                  <Input
                    type="number"
                    value={row.value}
                    onChange={(e) => updateValue(i, e.target.value)}
                    className="h-7 text-xs w-16 flex-shrink-0"
                    min="0"
                  />
                  {chartData.length > 2 && (
                    <button onClick={() => removeRow(i)} className="text-gray-400 hover:text-red-500 flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Axis titles (axis charts only) */}
          {!isPie && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Axis Titles</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-gray-500">X-axis</Label>
                  <Input
                    placeholder="e.g. Months"
                    value={xAxisTitle}
                    onChange={(e) => setXAxisTitle(e.target.value)}
                    className="h-7 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-gray-500">Y-axis</Label>
                  <Input
                    placeholder="e.g. Sales"
                    value={yAxisTitle}
                    onChange={(e) => setYAxisTitle(e.target.value)}
                    className="h-7 text-xs"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Display Options */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Display Options</Label>
            <div className="space-y-2">
              {[
                { label: 'Show Grid Lines',   value: showGridLines,   setter: setShowGridLines,   hide: isPie },
                { label: 'Show Data Labels',  value: showDataLabels,  setter: setShowDataLabels },
                { label: 'Show Legend',       value: showLegend,      setter: setShowLegend },
              ].filter(o => !o.hide).map(({ label, value, setter }) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">{label}</span>
                  <ToggleSwitch size="sm" enabled={value} onToggle={() => setter(!value)} />
                </div>
              ))}
              {showLegend && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Legend Position</span>
                  <Select value={legendPosition} onValueChange={setLegendPosition}>
                    <SelectTrigger className="h-7 w-28 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bottom">Bottom</SelectItem>
                      <SelectItem value="top-right">Top Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Line/Area Color */}
          {!isPie && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold">
                {chartType === 'line' || chartType === 'area' ? 'Line Color' : 'Accent Color'}
              </Label>
              <div className="flex gap-2 items-center">
                <input type="color" value={chartColor}
                  onChange={(e) => setChartColor(e.target.value)}
                  className="w-8 h-8 rounded border cursor-pointer" />
                <Input value={chartColor} onChange={(e) => setChartColor(e.target.value)}
                  className="flex-1 h-8 text-xs" />
              </div>
            </div>
          )}

          {/* Background */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Background</Label>
            <Select value={chartBackground} onValueChange={setChartBackground}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="#ffffff">White</SelectItem>
                <SelectItem value="#f3f4f6">Light Gray</SelectItem>
                <SelectItem value="transparent">Transparent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Mini Preview */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Preview</Label>
            <div
              className="h-20 rounded-lg border flex items-end justify-center gap-1 px-3 py-2 overflow-hidden"
              style={{ backgroundColor: chartBackground === 'transparent' ? '#f9fafb' : chartBackground }}
            >
              {!isPie ? (
                chartData.map((row, i) => (
                  <div key={i} className="rounded-t-sm flex-1 transition-all"
                    style={{
                      height: `${Math.max(4, (row.value / maxVal) * 100)}%`,
                      backgroundColor: dataColors[i] || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
                      minWidth: 6,
                    }}
                  />
                ))
              ) : (
                <div className="flex items-center justify-center h-full w-full">
                  <PieChart className="w-10 h-10" style={{ color: dataColors[0] || chartColor }} />
                </div>
              )}
            </div>
          </div>

          <Button onClick={handleAddChart} className="w-full bg-purple-600 hover:bg-purple-700"
            data-testid="add-chart-btn">
            <BarChart3 className="w-4 h-4 mr-2" />
            Add Chart
          </Button>
        </div>
      </ScrollArea>
    </div>
  );
};

export default ChartPanel;
